import { initScene } from './scene.js'
import {
  initAR,
  endSession,
  getMode,
  setMode,
  clearSketches,
  clearRuin,
  toggleRuinVisible,
  ruinIsBuilt,
  Modes
} from './ar-controller.js'
import {
  initUI,
  setMode as setUIMode,
  setRuinHint,
  showAROverlay,
  hideAROverlay,
  setSupportHint,
  setHitState,
  showPlacedHint,
  setPlaneStatus,
  setPlaneStatusVisible
} from './ui.js'

const enterBtn = document.getElementById('enter-ar-btn')

async function checkSupport() {
  if (!window.isSecureContext) {
    setSupportHint('AR requires HTTPS. Open this page over https://.', true)
    return false
  }
  if (!navigator.xr) {
    setSupportHint('WebXR not available. Use Chrome on Android with ARCore.', true)
    return false
  }
  try {
    const supported = await navigator.xr.isSessionSupported('immersive-ar')
    if (!supported) {
      setSupportHint('AR not supported. Install Google Play Services for AR.', true)
      return false
    }
  } catch (err) {
    setSupportHint(`AR check failed: ${err.message}`, true)
    return false
  }
  setSupportHint('Ready.')
  enterBtn.disabled = false
  return true
}

async function startAR() {
  enterBtn.disabled = true

  const { scene, camera, renderer, reticle, modelGroup } = initScene()

  // Cycle order: RUIN → PLACE → SKETCH → RUIN
  // Ruin is the headline demo mode and the default when AR starts.
  const MODE_CYCLE = [Modes.RUIN, Modes.PLACE, Modes.SKETCH]

  initUI({
    getModelGroup: () => {
      // 👁 in ruin mode toggles the ruin's visibility instead.
      // (Returning null to the caller skips the model-group toggle.)
      if (getMode() === Modes.RUIN && ruinIsBuilt()) {
        toggleRuinVisible()
        return null
      }
      return modelGroup
    },
    onExit: () => endSession(),
    onModeToggle: () => {
      const idx = MODE_CYCLE.indexOf(getMode())
      const next = MODE_CYCLE[(idx + 1) % MODE_CYCLE.length]
      setMode(next)
    },
    onClearSketches: () => {
      // 🗑 dispatches based on the active mode.
      const mode = getMode()
      if (mode === Modes.RUIN) {
        clearRuin()
      } else {
        clearSketches()
      }
    }
  })

  showAROverlay()

  try {
    await initAR({
      scene,
      camera,
      renderer,
      reticle,
      modelGroup,
      onHitStateChange: setHitState,
      onModeChange: (mode) => setUIMode(mode),
      onPlanesChanged: (counts, hasCorner) => setPlaneStatus(counts, hasCorner),
      onRuinStateChange: (_state, hint) => setRuinHint(hint),
      onDemoModeChange: (on) => {
        // Hide / restore the HUD + Phase 1 placeholder when the ruin is
        // built (or cleared). Walls + rubble live in their own group
        // and stay visible.
        setPlaneStatusVisible(!on)
        if (modelGroup) modelGroup.visible = !on
      },
      onPlace: (wasFirstPlacement) => {
        if (wasFirstPlacement) showPlacedHint()
      },
      onSessionEnd: () => {
        // Critical: the WebGL canvas is z-index:1, fixed-positioned over
        // the start-screen. If we don't take it out of the DOM, it
        // intercepts every touch and the start-screen looks frozen.
        if (renderer.domElement && renderer.domElement.parentNode) {
          renderer.domElement.parentNode.removeChild(renderer.domElement)
        }
        // Fully release the GL context so the next session can create a
        // fresh renderer cleanly. dispose() alone leaks the underlying
        // context on some devices, which can break re-entry to AR.
        try {
          renderer.forceContextLoss()
        } catch (_) {
          /* ignore */
        }
        try {
          renderer.dispose()
        } catch (_) {
          /* ignore */
        }
        hideAROverlay()
        enterBtn.disabled = false
      }
    })
    // Sync UI to the controller's default mode (RUIN). setMode in the
    // controller doesn't fire onModeChange for the initial value, so
    // push it explicitly once initAR has finished setting things up.
    setUIMode(getMode())
  } catch (err) {
    console.error('[main] AR session failed', err)
    hideAROverlay()
    setSupportHint(`AR session failed: ${err.message}`, true)
    enterBtn.disabled = false
  }
}

enterBtn.addEventListener('click', startAR)
checkSupport()
